import PropTypes from "prop-types";
import { useFormikContext } from "formik";
import { TextField, FormControl, FormHelperText } from "@mui/material";
import {
  getTextFieldStyles,
  labelStyles,
  formControlStyles,
  helperTextStyles
} from "./component-styles";
import "./readonly-styles.css";

const getPath = name => name.split(".");

const getNestedValue = (obj, path) => {
  return path.reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

const TextInput = ({ label, name, ...rest }) => {
  const { values, handleBlur, handleChange, errors, touched } =
    useFormikContext();
  const namePath = getPath(name);
  const isTouched = getNestedValue(touched, namePath);
  const errorMessage = getNestedValue(errors, namePath);
  const value = getNestedValue(values, namePath);

  const error = isTouched && errorMessage;
  const errorId = `${name}-error`;

  const handleInputChange = e => {
    handleChange(e);
  };

  // Combine existing aria-describedby with error ID
  const getAriaDescribedBy = () => {
    const existingDescribedBy = rest["aria-describedby"];
    if (error && existingDescribedBy) {
      return `${errorId} ${existingDescribedBy}`;
    } else if (error) {
      return errorId;
    }
    return existingDescribedBy;
  };

  // eslint-disable-next-line no-unused-vars
  const { "aria-describedby": _ariaDescribedBy, ...restWithoutAriaDescribedBy } = rest;

  return (
    <FormControl sx={formControlStyles} fullWidth error={!!error}>
      <TextField
        label={label}
        name={name}
        value={value || ""}
        onChange={handleInputChange}
        onBlur={handleBlur}
        error={!!error}
        sx={getTextFieldStyles(!!error)}
        inputProps={{
          'aria-invalid': !!error,
          'aria-describedby': getAriaDescribedBy()
        }}
        slotProps={{
          inputLabel: {
            sx: {
              ...labelStyles,
              ...(!!error && { color: "#b3261e" })
            },
            shrink: true
          }
        }}
        {...restWithoutAriaDescribedBy}
      />
      {error && (
        <FormHelperText id={errorId} sx={helperTextStyles}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
};

TextInput.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired
};

export default TextInput;
